import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, stationLabel } from "@/lib/api";
import { setStation } from "@/lib/cart";
import { Umbrella, ChevronRight, Utensils, BedDouble, Star } from "lucide-react";

const TYPES = [
  { key: "umbrella", label: "Ombrellone", Icon: Umbrella },
  { key: "table", label: "Tavolo", Icon: Utensils },
  { key: "sunbed", label: "Lettino", Icon: BedDouble },
  { key: "vip", label: "Area VIP", Icon: Star },
];

const SEARCH_PLACEHOLDERS = {
  umbrella: "Cerca numero ombrellone",
  table: "Cerca numero tavolo",
  sunbed: "Cerca numero lettino",
  vip: "Cerca area VIP",
};

export default function CustomerHome() {
  const [type, setType] = useState(null);
  const [stations, setStations] = useState([]);
  const [settings, setSettings] = useState(null);
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!type) return;
    api.get(`/public/stations?type=${type}`).then((r) => setStations(r.data));
  }, [type]);

  useEffect(() => {
    if (type && searchRef.current) {
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [type]);

  const pick = (s) => {
    setStation(s);
    nav(`/order/${s.token}`);
  };

  const filtered = stations.filter((s) => s.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="min-h-screen">
      <header className="px-6 pt-10 pb-6">
        <div className="eyebrow" data-testid="brand-eyebrow">Cervia · Riviera Adriatica</div>
        <h1 className="mt-3 text-5xl sm:text-6xl leading-none">Sarabi Beach</h1>
        <p className="mt-3 text-ink-500 max-w-md">
          Benvenuto. Seleziona la tua postazione per iniziare a ordinare direttamente dal tuo ombrellone, tavolo, lettino o area VIP.
        </p>
        {settings && !settings.service_open && (
          <div className="mt-4 rounded-2xl bg-bronze-600/10 border border-bronze-500/20 p-4 text-bronze-700">
            <div className="font-medium">Servizio momentaneamente sospeso</div>
            {settings.service_message && <div className="text-sm mt-1">{settings.service_message}</div>}
          </div>
        )}
      </header>

      <section className="px-6 pb-24">
        <div className="eyebrow mb-3">Scansiona il QR o scegli qui sotto</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {TYPES.map(({ key, label, Icon }) => {
            const active = type === key;
            return (
              <button
                key={key}
                data-testid={`station-type-${key}`}
                onClick={() => { setType(key); setQuery(""); }}
                className={`card-elevated p-4 flex flex-col items-start gap-2 text-left transition-colors ${
                  active ? "bg-bronze-600 border-bronze-600 text-white" : "bg-white hover:bg-sand-100"
                }`}
              >
                <Icon className={`w-6 h-6 ${active ? "text-white" : "text-bronze-600"}`} strokeWidth={1.5} />
                <div className="font-serif text-lg leading-none">{label}</div>
              </button>
            );
          })}
        </div>

        {type && (
          <div className="mt-6">
            <input
              key={type}
              ref={searchRef}
              autoFocus
              data-testid="station-search"
              placeholder={SEARCH_PLACEHOLDERS[type]}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full rounded-full border border-sand-300 bg-white px-5 py-3 text-base focus:outline-none focus:border-bronze-600"
            />
            <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 md:grid-cols-8 gap-2">
              {filtered.map((s) => (
                <button
                  key={s.id}
                  data-testid={`station-pick-${s.id}`}
                  onClick={() => pick(s)}
                  className="card-elevated py-4 text-center hover:border-bronze-600 hover:text-bronze-600 transition-colors font-serif text-xl"
                >
                  {s.name}
                </button>
              ))}
              {filtered.length === 0 && <div className="col-span-full text-ink-500 text-sm">Nessuna postazione</div>}
            </div>
          </div>
        )}
      </section>

      <footer className="fixed bottom-0 left-0 right-0 glass-cart px-6 py-3 flex items-center justify-between text-xs text-ink-500">
        <div>© Sarabi Beach</div>
        <Link to="/staff/login" data-testid="staff-login-link" className="inline-flex items-center gap-1 hover:text-bronze-600">
          Area staff <ChevronRight className="w-3 h-3" />
        </Link>
      </footer>
    </div>
  );
}
